import { MutableRefObject, useEffect, useRef, useState } from "react";
import { extend, Extent, buffer, createEmpty, getCenter } from "ol/extent";
import { Vector as VectorSource, TileWMS, XYZ } from "ol/source";
import TileGrid from "ol/tilegrid/TileGrid";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { Feature, View, Map } from "ol";
import { Point } from "ol/geom";
import { defaults as defaultControls } from "ol/control";
import { getDrawingSymbol, getPolygonSymbol } from "./symbols";
import { IPubEventsMapProps } from "../Providers/mapProvider";
import { BasemapButton } from "./Buttons/basemapButton";
import { LocationButton } from "./Buttons/GeoLocation";
import { HomeButton } from "./Buttons/Home";
import { AttributionButton } from "./Buttons/Attribution";
import { config } from "./config";
import { useAppContext } from "../Contexts/AppContext";
import { Basemaps } from "./enums";
import { LayersButton } from "./Buttons/Layers";
import { ScaleControl } from "./Buttons/Scale";
import { DrawingButton } from "./Buttons/Drawing";
import { useRegistrationContext } from "../Contexts/RegistrationContext";
import { SearchButton } from "./Buttons/Search";
import { ImageArcGISRest } from "ol/source";
import { Image as ImageLayer } from "ol/layer";
import { getPointResolution } from "ol/proj";

const DEFAULT_DPI = 25.4 / 0.28;
const inchesPerMeter = 1000 / 25.4;

const clusterLayerId = config.default.clusterLayerId;
const polygonLayerId = config.default.polygonLayerId;

let _map: MutableRefObject<Map>;
let sourceRef: MutableRefObject<VectorSource>;
let polygonSourceRef: MutableRefObject<VectorSource>;

export const useMap = (target: MutableRefObject<HTMLDivElement>, props: IPubEventsMapProps): Map => {
    _map = useRef<Map>();
    sourceRef = useRef<VectorSource>(new VectorSource());
    polygonSourceRef = useRef<VectorSource>(new VectorSource());

    const {
        hasMap,
        setBasemap,
        zoomToSourceFeatures,
        locateFeature,
        setZoomToSourceFeatures,
        toggleLayersMenuVisible,
        toggleSearchVisible,
        layerToToggle
    } = useAppContext();

    const { toggleDrawingMenuVisible, drawnFeatures } = useRegistrationContext();

    useEffect(() => {
        if (target.current && hasMap) {
            const layers = [];
            const tileGrid = new TileGrid(config.basemapLVM.tileGrid);
            const mammaLayer = new TileLayer({
                source: new XYZ({
                    url: config.basemapLVM.source.url,
                    maxZoom: config.basemapLVM.source.maxZoom,
                    projection: config.basemapLVM.source.projection,
                    tileGrid,
                    crossOrigin: config.basemapLVM.source.crossOrigin
                }),
                visible: true,
                id: Basemaps.Mamma
            });
            layers.push(mammaLayer);

            const ortoLayer = new TileLayer({
                source: new TileWMS({
                    url: config.basemapOrto.source.url,
                    params: config.basemapOrto.source.params,
                    projection: config.basemapOrto.source.projection,
                    serverType: config.basemapOrto.source.serverType,
                    crossOrigin: config.basemapOrto.source.crossOrigin
                }),
                visible: false,
                id: Basemaps.Orto
            });
            layers.push(ortoLayer);

            config.layers.map((layer) => {
                layer.visible &&
                    layers.push(
                        new ImageLayer({
                            id: layer.id,
                            source: new ImageArcGISRest({
                                url: layer.url,
                                crossOrigin: layer.crossOrigin,
                                attributions: layer.copyright
                            })
                        })
                    );
            });

            const drawingLayerSource = new VectorSource();
            const drawingLayer = new VectorLayer({
                id: config.default.drawingLayerId,
                source: drawingLayerSource,
                style: (drawFeature) => getDrawingSymbol(drawFeature.get("DRAWTYPE"), drawFeature.get("NR"))
            });
            layers.push(drawingLayer);

            const searchResultLayer = new VectorLayer({
                id: config.default.searchResultLayerId,
                source: new VectorSource(),
                style: () => getPolygonSymbol()
            });
            layers.push(searchResultLayer);

            _map.current = new Map({
                layers,
                target: target.current,
                view: new View({
                    projection: config.default.projection,
                    center: config.default.center,
                    zoom: config.default.zoom,
                    enableRotation: false,
                    minZoom: config.default.minZoom,
                    maxZoom: config.default.maxZoom
                }),
                controls: defaultControls({ zoom: false, rotate: false, attribution: false }).extend([
                    new BasemapButton(toggleBasemap),
                    new LocationButton(),
                    new HomeButton(),
                    new LayersButton(toggleLayersMenuVisible),
                    new SearchButton(toggleSearchVisible),
                    new AttributionButton(),
                    new ScaleControl(),
                    new DrawingButton(toggleDrawingMenuVisible)
                ])
            });

            _map.current.on("moveend", calculateScale);

            _map.current.on("pointermove", (evt) => {
                if (evt.dragging) {
                    return;
                }

                const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel);

                target.current.style.cursor = hasFeatureAtPixel ? "pointer" : null;
            });

            _map.current.on("click", (evt) => {
                if (evt.dragging) {
                    return;
                }
                const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel);
                if (!hasFeatureAtPixel) {
                    return;
                }
                console.log("map click");
            });

            _map.current.once("postcompose", () => {
                drawnFeatures && drawnFeatures.length ? zoomToExtent(drawingLayerSource.getExtent()) : zoomHome();
            });

            if (drawnFeatures && drawnFeatures.length) {
                drawnFeatures.map((f) => f.setStyle(getDrawingSymbol(f.get("DRAWTYPE"), f.get("NR"))));
                drawingLayerSource.addFeatures(drawnFeatures);
            }
        }
    }, [hasMap]);

    const toggleBasemap = (mapId: Basemaps) => {
        _map.current
            .getLayers()
            .getArray()
            .map((layer) => {
                const id = layer.get("id");
                id === Basemaps.Orto
                    ? layer.set("visible", mapId === Basemaps.Mamma)
                    : id === Basemaps.Orto
                    ? layer.set("visible", mapId === Basemaps.Orto)
                    : null;
            });
        setBasemap(mapId === Basemaps.Orto ? Basemaps.Mamma : Basemaps.Orto);
    };

    useEffect(() => {
        if (props.features && props.features.length) {
            sourceRef.current.clear();
            sourceRef.current.addFeatures(props.features);
            polygonSourceRef.current.clear();
            polygonSourceRef.current.addFeatures(props.features);
        } else {
            sourceRef.current.clear();
            polygonSourceRef.current.clear();
        }
    }, [props.forceUpdate, props.features]);

    useEffect(() => {
        _map.current &&
            _map.current.once("postcompose", () => {
                zoomToSourceFeatures === 1 && zoomHome(() => setZoomToSourceFeatures(null));
                zoomToSourceFeatures > 1 &&
                    zoomToExtent(sourceRef.current.getExtent(), () => setZoomToSourceFeatures(null));
            });
    }, [zoomToSourceFeatures]);

    useEffect(() => {
        if (locateFeature && _map.current) {
            // const layer = _map.current
            //     .getLayers()
            //     .getArray()
            //     .find((layer) => layer.get("id") === config.default.searchResultLayerId);
            // const source = layer.getSource();
            // source.clear();
            // source.addFeature(locateFeature.feature);
            zoomToExtent(locateFeature.feature.getGeometry().getExtent());
        }
    }, [locateFeature]);

    useEffect(() => {
        if (!layerToToggle || !layerToToggle.layerId) {
            return;
        }

        const layerConfig = config.layers.find((config) => config.id === layerToToggle.layerId);

        if (!layerToToggle.visible) {
            _map.current.removeLayer(
                _map.current
                    .getLayers()
                    .getArray()
                    .find((layer) => layer.get("id") === layerConfig.id)
            );
            return;
        }

        const alreadyAdded = _map.current
            .getLayers()
            .getArray()
            .find((layer) => layer.get("id") === layerToToggle.layerId);

        !alreadyAdded &&
            _map.current.addLayer(
                new ImageLayer({
                    id: layerConfig.id,
                    source: new ImageArcGISRest({
                        url: layerConfig.url,
                        crossOrigin: layerConfig.crossOrigin,
                        attributions: layerConfig.copyright
                    })
                })
            );
    }, [layerToToggle]);

    //@ts-ignore
    window.map = _map.current;
    return _map.current;
};

const calculateScale = () => {
    const view = _map.current.getView();
    const pointResolution = getPointResolution(view.getProjection(), view.getResolution(), view.getCenter(), "m");
    //@ts-ignore
    document.mapScale = Math.round(parseFloat(pointResolution.toString()) * inchesPerMeter * DEFAULT_DPI);
};

const zoomHome = (callback?: CallableFunction) => {
    const view = _map.current.getView();
    const resolutionX = config.default.extentSize[0] / view.viewportSize_[0];
    const resolutionY = config.default.extentSize[1] / view.viewportSize_[1];
    const zoom = view.getZoomForResolution(resolutionX > resolutionY ? resolutionX : resolutionY);

    view.fit(new Point(config.default.center), {
        maxZoom: zoom,
        duration: 1000,
        callback
    });
};

const zoomToExtent = (extent: Extent, callback?: CallableFunction) => {
    if (!extent || extent.includes(Infinity)) {
        return;
    }
    document.dispatchEvent(new Event("stopTracking"));
    const view = _map.current.getView();
    view.fit(extent, { duration: 500, padding: [100, 100, 100, 100], callback });
};

const zoomToFeature = (feature: Feature): void => {
    const view = _map.current.getView();
    const geometry = feature.getGeometry();
    const currentZoom = view.getZoom();
    const maxZoom = currentZoom > config.default.zoomToLevel ? currentZoom : config.default.zoomToLevel;
    document.dispatchEvent(new Event("stopTracking"));
    view.fit(geometry, { duration: 500, maxZoom });
};
